import { Injectable, inject, signal } from '@angular/core';
import { TagResponse, TagsApi } from '@tourfold/libraries/tf-api-client-public-v2';

/**
 * Shared cache of the tenant's tags, used by both the Tags display renderer (to resolve IDs to
 * name + color chips) and the Tags input renderer (to populate the picker). A small page-scoped
 * cache avoids per-relation refetching when several Tags fields render on the same page.
 *
 * Refresh semantics:
 *  - `ensureLoaded()` is idempotent; concurrent callers share the in-flight promise.
 *  - `refresh()` is called by the input renderer when the user opens the picker — captures any
 *    new tags created in another session without forcing a full app reload.
 *  - `insert()` lets the inline-create flow add a freshly POSTed tag without a refetch round-trip.
 */
@Injectable({ providedIn: 'root' })
export class SystemTagsCache {
  private readonly tagsApi = inject(TagsApi);
  private readonly tags$ = signal<readonly TagResponse[]>([]);
  private readonly loaded$ = signal(false);
  private loadingPromise: Promise<void> | null = null;

  readonly tags = this.tags$.asReadonly();
  readonly loaded = this.loaded$.asReadonly();

  async ensureLoaded(): Promise<void> {
    if (this.loaded$()) return;
    if (this.loadingPromise) return this.loadingPromise;
    this.loadingPromise = this.fetchAndStore().finally(() => {
      this.loadingPromise = null;
    });
    return this.loadingPromise;
  }

  async refresh(): Promise<void> {
    await this.fetchAndStore();
  }

  insert(tag: TagResponse): void {
    this.tags$.update((prev) => [...prev, tag]);
  }

  private async fetchAndStore(): Promise<void> {
    const list = await this.tagsApi.getTags({});
    this.tags$.set(list.items ?? []);
    this.loaded$.set(true);
  }
}
